import React, { useState, useMemo, useEffect } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { confirmDistribution, fetchFolioDataService } from "./PaymentView.service";
import { useParams } from "react-router-dom";
import './PaymentView.scss';
import { desEncriptar } from "../../../utilidades/encriptacion";
import { Dropdown } from "primereact/dropdown";


const PaymentView = () => {
    const { folioId } = useParams();
    const [folioData, setFolioData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [assignedAmounts, setAssignedAmounts] = useState({});
    const toastRef = React.useRef(null);
    const [sent, setSent] = useState(false);
    const isFirstLoad = React.useRef(true);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const accounts = [
        { name: 'BANCOMER TPS', code: 1 },
        { name: 'BANREGIO TPS', code: 42 },
    ];

    const permissions = JSON.parse(desEncriptar(sessionStorage.getItem('dataClient'), process.env.REACT_APP_CLAVE_INCRIPTACION));

    // Cargar datos del folio desde el backend
    useEffect(() => {
        const fetchFolioData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Llamada a la API - Endpoint actualizado
                const result = await fetchFolioDataService(folioId);

                // Validar estructura de datos
                if (!result.success || !result.data) {
                    throw new Error('Estructura de datos inválida');
                }

                const data = result.data;

                if (!data.folioNumber || !data.totalPaid || !Array.isArray(data.services)) {
                    throw new Error('No se ha registrado ningún pago respecto a ese número de folio');
                }

                setFolioData(data);

                // Inicializar montos asignados
                const initialAmounts = data.services.reduce((acc, service) => {
                    acc[service.id] = service.currentAmount || 0;
                    return acc;
                }, {});

                setAssignedAmounts(initialAmounts);

                // ✅ NUEVA FUNCIONALIDAD: Verificar condición solo en la primera carga
                if (isFirstLoad.current) {
                    // Calcular el total de pagos ya registrados
                    const totalPayment = data.services.reduce((sum, service) =>
                        sum + (service.payment || 0), 0
                    );

                    // Calcular el total de montos asignados iniciales
                    const totalInitialAssigned = Object.values(initialAmounts).reduce((sum, amount) =>
                        sum + (amount || 0), 0
                    );

                    // Calcular el total asignado y el restante
                    const totalAssigned = totalInitialAssigned + totalPayment;
                    const remaining = data.totalPaid - totalAssigned;

                    // Si el restante es menor a 0.01, marcar como enviado
                    if (Math.abs(remaining) < 0.01) {
                        setSent(true);
                    }

                    // Marcar que ya no es la primera carga
                    isFirstLoad.current = false;
                }

            } catch (err) {
                console.error('Error al cargar datos del folio:', err);
                setError(err.message);
                toastRef.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudo cargar la información del folio',
                    life: 5000
                });
            } finally {
                setLoading(false);
            }
        };

        if (folioId) {
            fetchFolioData().then(r => {});
        }
    }, [folioId]);

    // Cálculos derivados
    const calculations = useMemo(() => {
        if (!folioData) return null;
        const total = Object.values(assignedAmounts).reduce((sum, amount) => sum + (amount || 0), 0);
        const totalPayment = folioData.services.reduce((sum, service) => sum + (service.payment || 0), 0);
        const totalAssigned = total + totalPayment;
        const remaining = folioData.totalPaid - totalAssigned;
        const percentage = (totalAssigned / folioData.totalPaid) * 100;
        const isComplete = folioData.clientId !== 4474 ? true : Math.abs(remaining) < 0.01;
        const isOverAssigned = remaining < -0.01;

        return {
            totalAssigned,
            remaining,
            percentage: Math.min(percentage, 100),
            isComplete,
            isOverAssigned
        };
    }, [assignedAmounts, folioData]);

    // Manejador de cambio de monto
    const handleAmountChange = (serviceId, value) => {
        setAssignedAmounts(prev => ({
            ...prev,
            [serviceId]: value || 0
        }));
    };

    useEffect(() => {
        console.log(selectedAccount);
    }, [selectedAccount]);

    // Confirmación del pago con envío al backend
    const handleConfirmPayment = async () => {
        if (!calculations.isComplete) {
            toastRef.current?.show({
                severity: 'warn',
                summary: 'Distribución Incompleta',
                detail: calculations.isOverAssigned
                    ? `Has asignado $${Math.abs(calculations.remaining).toFixed(2)} de más`
                    : `Faltan $${calculations.remaining.toFixed(2)} por distribuir`,
                life: 5000
            });
            return;
        }

        try {
            setSubmitting(true);

            // Preparar payload para el backend
            const distribution = folioData.services
                .map(service => {
                    const assigned = assignedAmounts[service.id] || 0;
                    const totalPaid = (service.payment || 0) + assigned;
                    const roundToTwo = (num) => Math.round(num * 100) / 100;
                    const difference = roundToTwo(totalPaid - service.price);
                    const isPaid = difference >= 0 ? 3 : 2;

                    return {
                        serviceId: service.id,
                        assignedAmount: assigned,
                        price: service.price,
                        pagado: isPaid,
                    };
                })
                .filter(item => item.assignedAmount > 0);

            const payload = {
                folioNumber: folioData.folioNumber,
                totalPaid: folioData.totalPaid,
                userId: permissions.user.ID,
                account: selectedAccount.code,
                distribution
            };

            // Enviar al backend - Endpoint actualizado
            await confirmDistribution(payload);

            // Mostrar éxito
            toastRef.current?.show({
                severity: 'success',
                summary: 'Pago Confirmado',
                detail: 'La distribución de pagos se ha guardado exitosamente',
                life: 5000
            });

            setSent(true);

        } catch (err) {
            console.error('Error al confirmar pago:', err);
            toastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: err.message || 'No se pudo confirmar el pago',
                life: 5000
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Templates para la tabla (igual que en versión original)
    const serviceBodyTemplate = (rowData) => {
        return (
            <div className="payment-service__info">
                <div className="payment-service__name">{rowData.name}</div>
                <div className="payment-service__description">{rowData.description}</div>
            </div>
        );
    };

    const suggestedAmountBodyTemplate = (rowData) => {
        return (
            <div className="payment-service__suggested-amount">
                ${rowData.price.toFixed(2)}
            </div>
        );
    };

    const paymentBodyTemplate = (rowData) => {
        return (
            <div className="payment-service__suggested-amount">
                ${rowData.payment.toFixed(2)}
            </div>
        );
    };

    const assignAmountBodyTemplate = (rowData) => {
        return (
            <InputNumber
                value={assignedAmounts[rowData.id]}
                onValueChange={(e) => handleAmountChange(rowData.id, e.value)}
                mode="currency"
                currency="MXN"
                locale="es-MX"
                min={0}
                max={folioData.totalPaid}
                minFractionDigits={2}
                maxFractionDigits={2}
                className="payment-service__amount-input"
                placeholder="0.00"
                disabled={submitting}
            />
        );
    };

    const getProgressBarColor = () => {
        if (!calculations) return 'info';
        if (calculations.isOverAssigned) return 'danger';
        if (calculations.isComplete) return 'success';
        return 'info';
    };

    const getStatusMessage = () => {
        if (!calculations) return null;

        if (calculations.isOverAssigned) {
            return {
                severity: 'error',
                text: `Has excedido el total por $${Math.abs(calculations.remaining).toFixed(2)}`
            };
        }
        if (calculations.isComplete) {
            return {
                severity: 'success',
                text: '¡Distribución completa! Puedes confirmar el pago.'
            };
        }
        return {
            severity: 'info',
            text: `Faltan $${calculations.remaining.toFixed(2)} por distribuir`
        };
    };

    // Estados de carga y error
    if (loading) {
        return (
            <div className="payment-view">
                <div className="payment-view__container">
                    <Card className="payment-view__loading">
                        <div style={{textAlign: 'center', padding: '3rem'}}>
                            <ProgressSpinner/>
                            <p style={{marginTop: '1rem', color: '#6b7280'}}>
                                Cargando información del folio...
                            </p>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    if (error || !folioData) {
        return (
            <div className="payment-view">
                <div className="payment-view__container">
                    <Card className="payment-view__error">
                        <Message
                            severity="error"
                            text={error || 'No se pudo cargar la información del folio'}
                        />
                        <Button
                            label="Reintentar"
                            icon="pi pi-refresh"
                            onClick={() => window.location.reload()}
                            style={{marginTop: '1rem'}}
                        />
                    </Card>
                </div>
            </div>
        );
    }

    const statusMessage = getStatusMessage();

    return (
        <div className="payment-view">
            <Toast ref={toastRef}/>

            <div className="payment-view__container">
                {/* Header con información del folio */}
                <Card className="payment-view__header">
                    <div className="payment-header__content">
                        <div className="payment-header__info">
                            <h1 className="payment-header__title">Distribución de Pago</h1>
                            <p className="payment-header__folio">Folio: <strong>{folioData.folioNumber}</strong></p>
                        </div>
                        <div className="payment-header__total">
                            <span className="payment-header__total-label">Total Pagado</span>
                            <span className="payment-header__total-amount">
                                ${folioData.totalPaid.toFixed(2)}
                            </span>
                        </div>
                    </div>
                </Card>

                {/* Barra de progreso y estado */}
                { folioData.clientId === 4474 && (
                    <Card className="payment-view__progress">
                        <div className="payment-progress__header">
                            <h3 className="payment-progress__title">Progreso de Distribución</h3>
                            <span className="payment-progress__stats">
                            ${calculations.totalAssigned.toFixed(2)} / ${folioData.totalPaid.toFixed(2)}
                        </span>
                        </div>

                        <ProgressBar
                            value={calculations.percentage}
                            className={`payment-progress__bar payment-progress__bar--${getProgressBarColor()}`}
                        />

                        <Message
                            severity={statusMessage.severity}
                            text={statusMessage.text}
                            className="payment-progress__message"
                        />
                    </Card>
                )}


                {/* Tabla de servicios */}
                <Card className="payment-view__services">
                    <h2 className="payment-services__title">Servicios del Folio</h2>
                    <Dropdown value={selectedAccount} onChange={(e) => setSelectedAccount(e.value)}
                              options={accounts}
                              optionLabel="name"
                              placeholder="Selecciona una cuenta"
                              className="payment-services__dropdown"
                    />
                    <DataTable
                        value={folioData.services}
                        responsiveLayout="scroll"
                        className="payment-services__table"
                        emptyMessage="No hay servicios disponibles"
                    >
                        <Column
                            field="name"
                            header="Servicio"
                            body={serviceBodyTemplate}
                            className="payment-services__column--service"
                        />
                        <Column
                            field="price"
                            header="Precio"
                            body={suggestedAmountBodyTemplate}
                            className="payment-services__column--suggested"
                        />
                        <Column
                            field="payment"
                            header="Abono"
                            body={paymentBodyTemplate}
                            className="payment-services__column--suggested"
                        />
                        <Column
                            header="Monto Asignado"
                            body={assignAmountBodyTemplate}
                            className="payment-services__column--assign"
                        />
                    </DataTable>
                </Card>

                {/* Resumen y acciones */}
                <Card className="payment-view__summary">
                    <div className="payment-summary__content">
                        <div className="payment-summary__totals">
                            <div className="payment-summary__row">
                                <span className="payment-summary__label">Total Pagado:</span>
                                <span className="payment-summary__value">
                                    ${folioData.totalPaid.toFixed(2)}
                                </span>
                            </div>
                            <div className="payment-summary__row">
                                <span className="payment-summary__label">Total Asignado:</span>
                                <span className="payment-summary__value payment-summary__value--highlight">
                                    ${calculations.totalAssigned.toFixed(2)}
                                </span>
                            </div>
                            <div className="payment-summary__row ">
                                <span className="payment-summary__label">Restante:</span>
                                <span className={`payment-summary__value ${
                                    calculations.isOverAssigned
                                        ? 'payment-summary__value--error'
                                        : calculations.isComplete
                                            ? 'payment-summary__value--success'
                                            : 'payment-summary__value--warning'
                                }`}>
                                    ${calculations.remaining.toFixed(2)}
                                </span>
                            </div>
                        </div>

                        <Button
                            label={sent ? "Ya enviado" : "Guardar y Enviar"}
                            icon={sent ? "pi pi-check" : "pi pi-save"}
                            onClick={handleConfirmPayment}
                            disabled={!calculations?.isComplete || submitting || sent}
                            loading={submitting}
                            className={sent ? "p-button-secondary" : "p-button-success"}
                            tooltip={
                                sent ? "Este folio ya fue procesado" :
                                    !calculations?.isComplete ? "Complete la asignación de montos" :
                                        ""
                            }
                            tooltipOptions={{ position: 'top' }}
                        />
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default PaymentView;
